// src/portal/portalMarkup.js
//
// THE REVIEWER'S FINDING VOCABULARY, imported from the server.
//
// planReviewMarkup.js has NO imports at all, so unlike planReviewSubmission.js
// it can be pulled into a browser bundle directly and needs no leaf extracted
// for it. ui/Dockerfile copies it in, the same way it copies submitterTags.js.
//
// ── WHY IT IS IMPORTED AND NOT TYPED ────────────────────────────────────────
//
// Decision 9 — the five markup types, each with optional geometry and a
// required note — is ABSENT from OPERATOR_PORTAL_DESIGN.md. The Phase 7 audit
// found the doc kept "markup is typed review features referencing a submitted
// feature" and dropped the vocabulary entirely. So planReviewMarkup.js is the
// ONLY record of what the five are, and a second copy here would not be a
// duplicate of a documented decision — it would be a competing source for an
// undocumented one. check-doc-facts now names both constants in its owner map.

import { MARKUP_TYPES, MARKUP_NOTE_MIN } from "../../../admin-api/lib/plan-review/planReviewMarkup.js";

export { MARKUP_TYPES, MARKUP_NOTE_MIN };

/**
 * UI copy, keyed by the server's vocabulary. NOT a second declaration: the
 * buttons are generated from MARKUP_TYPES and this only supplies text, with the
 * raw type as the fallback. A test asserts every member has an entry, so a
 * sixth type added server-side surfaces as a failing assertion rather than a
 * button labelled `information_missing`.
 */
export const MARKUP_LABEL = {
  setback_violation: "Setback violation",
  surface_conflict: "Surface conflict",
  reroute_required: "Reroute required",
  information_missing: "Information missing",
  other: "Other",
};

/** @returns {boolean} true only for a member of MARKUP_TYPES. */
export function isMarkupType(value) {
  return typeof value === "string" && MARKUP_TYPES.includes(value);
}

/**
 * Is this note long enough for the server to accept it?
 *
 * The SAME rule route 9 applies — btrim, then length — so the form can say so
 * BEFORE submit rather than turning it into a refusal after. `note_too_short`
 * exists and has copy, but a reviewer who has written nine characters should be
 * told by the counter, not by a round trip.
 */
export function noteIsLongEnough(note) {
  return typeof note === "string" && note.trim().length >= MARKUP_NOTE_MIN;
}

/**
 * Findings grouped by the feature they reference, for the list beside the map.
 *
 * A feature can carry MORE THAN ONE finding: route 9 allocates ordinal as
 * max+1 per REVISION, not per feature, so nothing stops a pad having both a
 * setback violation and a missing survey. That is correct and is not to be
 * prevented -- but it is why the map shows a COUNT rather than three outlines
 * (§3.25) and why this exists.
 *
 * @returns {Map<number, Array>} feature_ordinal -> findings, insertion-ordered
 */
export function groupByFeature(markup = []) {
  const out = new Map();
  for (const m of markup) {
    const key = m?.feature_ordinal;
    if (!Number.isInteger(key)) continue;
    if (!out.has(key)) out.set(key, []);
    out.get(key).push(m);
  }
  return out;
}

/** How many findings reference this feature. 0 when none do. */
export function findingCountFor(markup, featureOrdinal) {
  return groupByFeature(markup).get(featureOrdinal)?.length ?? 0;
}
